import React, { useEffect, useMemo, useState } from "react";

function parseNum(v) {
  const s = String(v ?? "")
    .trim()
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}
function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}
function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}
function formatPct(n) {
  return `${n.toFixed(2)} %`;
}

// Calcul de la mensualité de crédit
function calcMonthlyPayment(principal, annualRatePct, months) {
  const P = principal;
  const n = months;
  const r = annualRatePct / 100 / 12;

  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / n;

  const denom = 1 - Math.pow(1 + r, -n);
  return (P * r) / denom;
}

// Barème taux auto (ajuste si besoin)
const DEFAULT_RATE_TABLE = [
  { years: 10, rate: 3.3 },
  { years: 15, rate: 3.45 },
  { years: 20, rate: 3.5 },
  { years: 25, rate: 3.61 },
  { years: 30, rate: 3.75 },
];

function interpolateRate(years, table) {
  const sorted = [...table].sort((a, b) => a.years - b.years);
  if (!sorted.length) return 0;

  if (years <= sorted[0].years) return sorted[0].rate;
  if (years >= sorted[sorted.length - 1].years)
    return sorted[sorted.length - 1].rate;

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (years >= a.years && years <= b.years) {
      const t = (years - a.years) / (b.years - a.years);
      return a.rate + t * (b.rate - a.rate);
    }
  }
  return sorted[0].rate;
}

export default function CalculRentabiliteLocative() {
  // Achat
  const [purchasePrice, setPurchasePrice] = useState("180000");
  const [notaryFees, setNotaryFees] = useState("13500");
  const [works, setWorks] = useState("0");
  const [furniture, setFurniture] = useState("0");

  // Calcul automatique des frais de notaire (7.5% du prix d'achat par défaut)
  useEffect(() => {
    const price = parseNum(purchasePrice);
    if (price > 0) {
      const calculatedNotaryFees = Math.round(price * 0.075);
      setNotaryFees(String(calculatedNotaryFees));
    }
  }, [purchasePrice]);

  // Location
  const [rentMonthly, setRentMonthly] = useState("900");

  // Charges (mensuelles)
  const [chargesMonthly, setChargesMonthly] = useState("0"); // copro, entretien, vacance, etc (simplifié)
  const [propertyTaxMonthly, setPropertyTaxMonthly] = useState("80"); // taxe foncière / 12
  const [insuranceMonthly, setInsuranceMonthly] = useState("20"); // PNO / GLI etc
  const [managementMonthly, setManagementMonthly] = useState("0"); // gestion locative

  // Crédit (optionnel)
  const [loanRate, setLoanRate] = useState("3.5"); // taux annuel du crédit
  const [loanDuration, setLoanDuration] = useState("20"); // durée en années
  const [useAutoRate, setUseAutoRate] = useState(true); // mode taux automatique
  const [loanMonthly, setLoanMonthly] = useState("0"); // mensualité crédit (si achat à crédit)
  const [loanInsuranceMonthly, setLoanInsuranceMonthly] = useState("0"); // assurance emprunteur

  // Calcul automatique du taux selon la durée
  useEffect(() => {
    if (useAutoRate) {
      const years = parseNum(loanDuration);
      const autoRate = interpolateRate(years, DEFAULT_RATE_TABLE);
      setLoanRate(String(autoRate.toFixed(2)));
    }
  }, [useAutoRate, loanDuration]);

  // Calcul automatique de la mensualité de crédit
  useEffect(() => {
    const price = parseNum(purchasePrice);
    const rate = parseNum(loanRate);
    const years = parseNum(loanDuration);

    if (price > 0 && years > 0) {
      const months = Math.round(years * 12);
      const monthlyPayment = calcMonthlyPayment(price, rate, months);
      setLoanMonthly(String(Math.round(monthlyPayment)));
    }
  }, [purchasePrice, loanRate, loanDuration]);

  // Impôts (mode simple)
  const [taxRatePct, setTaxRatePct] = useState("0"); // pour estimer un "net net" (option)

  const computed = useMemo(() => {
    const price = clamp(parseNum(purchasePrice), 0, 100_000_000);
    const notary = clamp(parseNum(notaryFees), 0, 10_000_000);
    const w = clamp(parseNum(works), 0, 50_000_000);
    const furn = clamp(parseNum(furniture), 0, 10_000_000);

    const rent = clamp(parseNum(rentMonthly), 0, 1_000_000);

    const ch = clamp(parseNum(chargesMonthly), 0, 1_000_000);
    const tf = clamp(parseNum(propertyTaxMonthly), 0, 1_000_000);
    const ins = clamp(parseNum(insuranceMonthly), 0, 1_000_000);
    const mgmt = clamp(parseNum(managementMonthly), 0, 1_000_000);

    const loan = clamp(parseNum(loanMonthly), 0, 1_000_000);
    const loanIns = clamp(parseNum(loanInsuranceMonthly), 0, 1_000_000);

    const taxRate = clamp(parseNum(taxRatePct), 0, 80) / 100;

    const investment = price + notary + w + furn;

    const annualRent = rent * 12;

    // Charges annuelles (simplifié)
    const annualCharges = (ch + tf + ins + mgmt) * 12;

    // Revenu net avant crédit/impôt
    const annualNetBeforeLoan = annualRent - annualCharges;

    // Rendements
    const grossYield = investment > 0 ? (annualRent / investment) * 100 : 0;
    const netYield =
      investment > 0 ? (annualNetBeforeLoan / investment) * 100 : 0;

    // Crédit
    const annualLoan = (loan + loanIns) * 12;

    // Impôt simplifié appliqué sur net avant crédit (approx)
    const annualTax = Math.max(0, annualNetBeforeLoan) * taxRate;

    // Cashflow
    const monthlyCashflow =
      rent -
      (ch + tf + ins + mgmt) -
      (loan + loanIns) -
      (Math.max(0, annualNetBeforeLoan) * taxRate) / 12;

    // Net après crédit + impôt
    const annualNetAfterAll = annualNetBeforeLoan - annualLoan - annualTax;

    return {
      investment,
      annualRent,
      annualCharges,
      annualNetBeforeLoan,
      grossYield,
      netYield,
      annualLoan,
      annualTax,
      annualNetAfterAll,
      monthlyCashflow,
      rent,
      ch,
      tf,
      ins,
      mgmt,
      loan,
      loanIns,
      taxRatePct: taxRate * 100,
    };
  }, [
    purchasePrice,
    notaryFees,
    works,
    furniture,
    rentMonthly,
    chargesMonthly,
    propertyTaxMonthly,
    insuranceMonthly,
    managementMonthly,
    loanRate,
    loanDuration,
    useAutoRate,
    loanMonthly,
    loanInsuranceMonthly,
    taxRatePct,
  ]);

  const canCompute = computed.investment > 0;

  return (
    <div className="page">
      <div className="hero">
        <h1>Rentabilité locative</h1>
        <p className="muted" style={{ margin: 0 }}>
          Rendement brut/net + cashflow (simple). Calcul 100% local.
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* Entrées */}
        <div className="card">
          <h2 className="cardTitle">Entrées</h2>

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Achat
          </h3>
          <label className="label">
            Prix d’achat (€)
            <input
              className="input"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Frais de notaire (€)
            <input
              className="input"
              value={notaryFees}
              onChange={(e) => setNotaryFees(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Travaux (€) (option)
            <input
              className="input"
              value={works}
              onChange={(e) => setWorks(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Mobilier (€) (option)
            <input
              className="input"
              value={furniture}
              onChange={(e) => setFurniture(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div style={{ height: 10 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Location
          </h3>
          <label className="label">
            Loyer mensuel (€)
            <input
              className="input"
              value={rentMonthly}
              onChange={(e) => setRentMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div style={{ height: 10 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Charges mensuelles
          </h3>
          <label className="label">
            Charges diverses (€) (copro/entretien/vacance…)
            <input
              className="input"
              value={chargesMonthly}
              onChange={(e) => setChargesMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Taxe foncière (/mois) (€)
            <input
              className="input"
              value={propertyTaxMonthly}
              onChange={(e) => setPropertyTaxMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Assurances (/mois) (€)
            <input
              className="input"
              value={insuranceMonthly}
              onChange={(e) => setInsuranceMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>
          <label className="label">
            Gestion locative (/mois) (€) (option)
            <input
              className="input"
              value={managementMonthly}
              onChange={(e) => setManagementMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div style={{ height: 10 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Crédit (option)
          </h3>
          <label className="label">
            Durée (années)
            <input
              className="input"
              value={loanDuration}
              onChange={(e) => setLoanDuration(e.target.value)}
              inputMode="numeric"
              placeholder="Ex: 20"
            />
          </label>
          <label className="label">
            Mode taux
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                className={`btn ${useAutoRate ? "" : "btnSecondary"}`}
                onClick={() => setUseAutoRate(true)}
              >
                Auto
              </button>
              <button
                type="button"
                className={`btn ${!useAutoRate ? "" : "btnSecondary"}`}
                onClick={() => setUseAutoRate(false)}
              >
                Manuel
              </button>
            </div>
            <div className="small" style={{ marginTop: 8 }}>
              {useAutoRate ? (
                <>Taux ajusté automatiquement selon la durée</>
              ) : (
                <>Saisis ton taux personnalisé</>
              )}
            </div>
          </label>
          <label className="label">
            Taux annuel (%)
            <input
              className="input"
              value={loanRate}
              onChange={(e) => setLoanRate(e.target.value)}
              inputMode="decimal"
              placeholder="Ex: 3.5"
              disabled={useAutoRate}
              style={
                useAutoRate
                  ? { opacity: 0.7, cursor: "not-allowed" }
                  : undefined
              }
            />
          </label>
          <label className="label">
            Mensualité crédit (€) (calculée automatiquement)
            <input
              className="input"
              value={loanMonthly}
              onChange={(e) => setLoanMonthly(e.target.value)}
              inputMode="decimal"
              style={{ background: "rgba(255,255,255,.05)" }}
            />
          </label>
          <label className="label">
            Assurance emprunteur (/mois) (€)
            <input
              className="input"
              value={loanInsuranceMonthly}
              onChange={(e) => setLoanInsuranceMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div style={{ height: 10 }} />

          <h3 style={{ margin: "0 0 10px 0", fontSize: 14, opacity: 0.9 }}>
            Impôts (option simple)
          </h3>
          <label className="label">
            Taux d’imposition (%) (0 si tu ne veux pas l’inclure)
            <input
              className="input"
              value={taxRatePct}
              onChange={(e) => setTaxRatePct(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn btnSecondary"
              onClick={() => {
                setPurchasePrice("180000");
                // Les frais de notaire seront calculés automatiquement via useEffect
                setWorks("0");
                setFurniture("0");
                setRentMonthly("900");
                setChargesMonthly("0");
                setPropertyTaxMonthly("80");
                setInsuranceMonthly("20");
                setManagementMonthly("0");
                setLoanDuration("20");
                setUseAutoRate(true);
                // Le taux et la mensualité de crédit seront calculés automatiquement via useEffect
                setLoanInsuranceMonthly("0");
                setTaxRatePct("0");
              }}
            >
              Réinitialiser
            </button>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Mode simple : pour une analyse fine, on peut ajouter vacance
            locative %, IR/PS, LMNP réel, etc.
          </div>
        </div>

        {/* Résultats */}
        <div className="card">
          <h2 className="cardTitle">Résultats</h2>

          {!canCompute ? (
            <p className="muted">Renseigne au minimum un prix d’achat.</p>
          ) : (
            <>
              <ResultRow
                label="Investissement total"
                value={formatEUR(computed.investment)}
              />
              <ResultRow
                label="Loyers annuels"
                value={formatEUR(computed.annualRent)}
              />
              <ResultRow
                label="Charges annuelles (estim.)"
                value={formatEUR(computed.annualCharges)}
              />

              <div style={{ height: 10 }} />

              <ResultRow
                label="Rendement brut"
                value={formatPct(computed.grossYield)}
              />
              <ResultRow
                label="Rendement net (avant crédit/impôt)"
                value={formatPct(computed.netYield)}
              />

              <div style={{ height: 10 }} />

              <ResultRow
                label="Cashflow mensuel (estim.)"
                value={formatEUR(computed.monthlyCashflow)}
              />

              <div style={{ height: 14 }} />

              <h3
                style={{
                  margin: "0 0 10px 0",
                  fontSize: 16,
                  letterSpacing: ".2px",
                }}
              >
                Détails
              </h3>

              <div
                className="card"
                style={{ padding: 12, background: "rgba(255,255,255,.06)" }}
              >
                <SmallLine
                  label="Loyer mensuel"
                  value={formatEUR(computed.rent)}
                />
                <SmallLine
                  label="Charges (diverses) /mois"
                  value={formatEUR(computed.ch)}
                />
                <SmallLine
                  label="Taxe foncière /mois"
                  value={formatEUR(computed.tf)}
                />
                <SmallLine
                  label="Assurances /mois"
                  value={formatEUR(computed.ins)}
                />
                <SmallLine
                  label="Gestion /mois"
                  value={formatEUR(computed.mgmt)}
                />
                <SmallLine
                  label="Crédit /mois"
                  value={formatEUR(computed.loan)}
                />
                <SmallLine
                  label="Ass. emprunteur /mois"
                  value={formatEUR(computed.loanIns)}
                />
                <SmallLine
                  label="Impôt (taux simple)"
                  value={`${computed.taxRatePct.toFixed(1)} %`}
                />
                <SmallLine
                  label="Impôts annuels estimés"
                  value={formatEUR(computed.annualTax)}
                />
                <SmallLine
                  label="Net annuel après crédit + impôt"
                  value={formatEUR(computed.annualNetAfterAll)}
                />
              </div>

              <div className="small" style={{ marginTop: 10 }}>
                Cashflow = loyer – charges – crédit – impôts (simplifié). Pour
                LMNP réel, le résultat peut changer.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value }) {
  return (
    <div
      className="resultRow"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SmallLine({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "6px 0",
      }}
    >
      <span className="muted" style={{ fontSize: 13 }}>
        {label}
      </span>
      <strong style={{ fontSize: 13 }}>{value}</strong>
    </div>
  );
}
